import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@utils/cn';

/**
 * Badge chrome is neutral in every status variant — only the 6px leading dot
 * is chromatic. A saturated fill would compete with the yellow action and
 * make routine states (out of stock, draft) read as alerts.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full bg-foreground/5 px-2.5 py-1 text-meta font-medium text-muted-foreground'
);

const dotVariants = cva('size-1.5 shrink-0 rounded-full', {
  variants: {
    variant: {
      success: 'bg-status-success',
      warning: 'bg-status-warning',
      danger: 'bg-status-danger',
      info: 'bg-status-info',
      neutral: 'bg-status-neutral',
      tag: 'bg-iris-violet',
      /** No dot — plain neutral chip. */
      plain: 'hidden'
    }
  },
  defaultVariants: {
    variant: 'neutral'
  }
});

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof dotVariants> {}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants(), className)} {...props}>
      <span aria-hidden className={cn(dotVariants({ variant }))} />
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
